/* eslint-disable max-lines */
import * as Yup from "yup";
import {
  ITripType,
  OperatorCommisionFeePaymentTypes,
  OperatorCommisionFeeValueTypes
} from "../../interfaces/endpoints/trips/Trips.types";
import { MAX_TRIP_DURATION_MINUTES } from "../tripDuration";

// yup 0.32 hands a test the values enclosing it as `from` (index 1 is the whole form), but its
// typings leave the field out.
type WithFrom = Yup.TestContext & { from?: { value: unknown }[] };
const formOf = (ctx: Yup.TestContext): Partial<ITripType> | undefined =>
  (ctx as WithFrom).from?.[1]?.value as Partial<ITripType> | undefined;

const commissionFeeSchema = Yup.object().nullable().shape({
  fee: Yup
    .number()
    .transform((value) => {
      if (isNaN(value)) return undefined;
      // Convert the input percentage to decimal for backend
      return parseFloat(value) / 100;
    })
    .test("is-valid-percentage", "Komisioni duhet të jetë midis 0% dhe 100%", (value) => {
      if (!value && value !== 0) return true;
      return value >= 0 && value <= 1;
    })
    .nullable(),
  paymentType: Yup
    .number()
    .oneOf(Object.values(OperatorCommisionFeePaymentTypes).filter((x) => typeof x === "number") as number[])
    .default(OperatorCommisionFeePaymentTypes.JourneyReservation),
  valueType: Yup
    .number()
    .oneOf(Object.values(OperatorCommisionFeeValueTypes).filter((x) => typeof x === "number") as number[])
    .default(OperatorCommisionFeeValueTypes.Percentage)
});

const TripsSchema = Yup.object().shape({
  // operationalInterval: Yup.object().test({
  //   name: "at-least-one-day-selected",
  //   message: "Zgjedh ditën/ditët e udhëtimit për orarin",
  //   test: (value) => {
  //     const days = [
  //       "monday",
  //       "tuesday",
  //       "wednesday",
  //       "thursday",
  //       "friday",
  //       "saturday",
  //       "sunday"
  //     ];
  //     return days.some((day) => value[day] === true);
  //   }
  // }),
  trip: Yup.object().shape({
    lineId: Yup.number()
      .typeError("Vendos linjën e orarit")
      .required("Vendos linjën e orarit"),
    departureTimeString: Yup.string().required("Vendos kohën e nisjes"),
    arrivalTimeString: Yup.string().required("Vendos kohën e arritjes"),
    // Length of the trip ("Kohëzgjatja") in minutes; may exceed 24h (multi-day
    // trips), capped at 96:00. The arrival time is derived from departure +
    // this, so this is the field the user actually fills.
    durationMinutes: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .integer("Kohëzgjatja duhet të jetë numër i plotë minutash")
      .min(1, "Kohëzgjatja duhet të jetë së paku 1 minutë")
      .max(
        MAX_TRIP_DURATION_MINUTES,
        "Kohëzgjatja nuk mund të kalojë 96:00 orë"
      )
      .required("Vendos kohëzgjatjen e udhëtimit (orë:minuta)"),
    isReservable: Yup.boolean().test(
      "Valuta",
      "Valuta duhet të jetë euro per te qene e rezervushme",
      (isReservable, ctx) => {
        if (isReservable && ctx.parent.defaultCurrencyId === 2) {
          return false;
        }
        return true;
      }
    )
  }),
  schedules: Yup.array().of(
    Yup.object().shape({
      stationId: Yup.number()
        .typeError("Vendos linjën e orarit")
        .required("Vendos stacionin e ndalesës")
        .test(
          "unique-station",
          "Ky stacion është zgjedhur edhe te një ndalesë tjetër",
          function (value: number | undefined) {
            const same = (formOf(this)?.schedules ?? []).filter(
              (stop) => stop && Number(stop.stationId) === value
            );
            return !(value && same.length > 1);
          }
        ),
      // departureTime: Yup.string().test(
      //   "Vendos kohën e nisjes nga stacioni",
      //   "Koha duhet të jetë më e madhe se koha e nisjes së linjës",
      //   (departureTime: any, ctx: any) => {
      //     if (departureTime) {
      //       return departureTime > ctx.parent.departureTime;
      //     }
      //     return false;
      //   }
      // )

      departureTimeString: Yup.string()
        .typeError("sheno dicka")
        .required("Vendos kohën e nisjes"),
      // The stop's distance from the departure. It must fall before the trip's arrival and no two
      // stops may share it; the server refuses both, this names them before the save.
      duration: Yup.number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .test(
          "inside-trip",
          "Ndalesa duhet të jetë para arritjes së udhëtimit",
          function (value: number | undefined) {
            const length = Number(formOf(this)?.trip?.durationMinutes);
            return !(value && length > 0 && value >= length);
          }
        )
        .test(
          "unique",
          "Dy ndalesa nuk mund të kenë të njëjtën kohë",
          function (value: number | undefined) {
            const same = (formOf(this)?.schedules ?? []).filter(
              (stop) => stop && Number(stop.duration) === value
            );
            return !(value && same.length > 1);
          }
        )
    })
  ),
  entityCommissionFee: commissionFeeSchema
});

export default TripsSchema;
